import { Form, Link, useMatches, useSubmit } from '@remix-run/react'
import { useRef } from 'react'
import { type Theme } from '#app/utils/theme.server.js'
import { type useOptionalUser } from '#app/utils/user.js'
import { SearchBar } from './search-bar'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx'
import { Icon } from './ui/icon'

export function AppHeader({
	user,
	theme,
}: {
	user: ReturnType<typeof useOptionalUser>
	theme: Theme | null
}) {
	const isAdmin = !!user?.roles.find(r => r.name === 'admin')
	const matches = useMatches()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	// const themeRef = useRef<HTMLFormElement>(null)
	// const fetcher = useFetcher<typeof action>()
	// const optimisticMode = useOptimisticThemeMode()
	// const mode = optimisticMode ?? theme ?? 'system'
	// const nextMode =
	// 	mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	// const nextItem =
	// 	nextMode === 'system' ? 'laptop' : nextMode === 'dark' ? 'moon' : 'sun'
	const isOnSearchPage = matches.find(
		m => m.id === 'routes/app+/library+/index',
	)
	const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />
	return (
		<header
			className={`relative z-30 flex h-20 w-full items-center justify-between border-b border-b-border bg-background px-4 font-mono uppercase tracking-wide lg:px-6`}
		>
			<div className="flex items-center justify-start xl:flex-1 xl:basis-1/4">
				<a
					href="https://kogia.org"
					target="_blank"
					className="ml-1 mr-3 block w-auto outline-offset-8 outline-ring"
					rel="noreferrer"
				>
					<img className="h-14 dark:hidden" src="/logo.png" alt="Kogia Logo" />
					<img
						className="hidden h-14 dark:block"
						src="/logo-dark.png"
						alt="Kogia Logo"
					/>
				</a>
			</div>
			<nav className="hidden w-full flex-1 basis-1/2 justify-center gap-2 md:flex lg:gap-3">
				<Button asChild variant={'ghost'} size="default">
					<Link
						className="!bg-transparent underline underline-offset-2 hover:!text-[#8B8B8B]"
						to="/"
					>
						Library
					</Link>
				</Button>
				<Button asChild variant={'ghost'} size="default">
					<a
						className="!bg-transparent hover:!text-[#8B8B8B]"
						href="https://www.kogia.org/projects"
					>
						Projects
					</a>
				</Button>
				<Button asChild variant={'ghost'} size="default">
					<a
						className="!bg-transparent hover:!text-[#8B8B8B]"
						href="https://www.kogia.org/fellowships"
					>
						Fellowships
					</a>
				</Button>
				<Button asChild variant={'ghost'} size="default">
					<a
						className="!bg-transparent hover:!text-[#8B8B8B]"
						href="https://www.kogia.org/about"
					>
						About
					</a>
				</Button>
			</nav>
			<div className="flex justify-end gap-2 xl:flex-1 xl:basis-1/4">
				{user && <div className="max-w-xl flex-1">{searchBar}</div>}
				{!user && (
					<div className="flex gap-3">
						<Button
							className="hidden rounded-[50px] px-6 font-mono md:flex"
							asChild
							variant={'outline'}
							size="lg"
						>
							<Link to="/login">Login</Link>
						</Button>
						<DropdownMenu>
							<DropdownMenuTrigger
								className="block border-0 border-none md:hidden"
								asChild
							>
								<Button
									variant="ghost"
									size="icon"
									className="flex items-center justify-center border-0 border-none"
								>
									<Icon
										name="hamburger-menu"
										className="flex text-body-lg md:hidden"
									>
										<span className="sr-only">Menu</span>
									</Icon>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuPortal>
								<DropdownMenuContent sideOffset={8} align="end">
									<DropdownMenuItem asChild>
										<Link className="md:hidden" prefetch="intent" to={`/`}>
											<Icon size="md" name="magnifying-glass">
												Library
											</Icon>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link
											className="md:hidden"
											prefetch="intent"
											to={`/contributors`}
										>
											<Icon size="md" name="avatar">
												Contributors
											</Icon>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<a
											className="md:hidden"
											href={`https://www.kogia.org/projects`}
										>
											<Icon size="md" name="external-link">
												Projects
											</Icon>
										</a>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<a
											className="md:hidden"
											href={`https://www.kogia.org/fellowships`}
										>
											<Icon size="md" name="external-link">
												Fellowships
											</Icon>
										</a>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<a
											className="md:hidden"
											href={`https://www.kogia.org/about`}
										>
											<Icon size="md" name="external-link">
												About
											</Icon>
										</a>
									</DropdownMenuItem>
									<DropdownMenuSeparator className="md:hidden" />
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/apply`}>
											<Icon size="md" name="avatar">
												Apply as a Member
											</Icon>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/contributor/apply`}>
											<Icon size="md" name="video">
												Become a Contributor
											</Icon>
										</Link>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenuPortal>
						</DropdownMenu>
					</div>
				)}
				{user && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button size="icon" variant="ghost">
								<Icon name="hamburger-menu" className="text-body-xl">
									<span className="sr-only">Menu</span>
								</Icon>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuPortal>
							<DropdownMenuContent sideOffset={8} align="end">
								<DropdownMenuItem className="flex" asChild>
									<Link prefetch="intent" to={`/`}>
										<Icon size="md" name="magnifying-glass">
											Library
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem className="flex" asChild>
									<Link prefetch="intent" to={`/contributors`}>
										<Icon size="md" name="video">
											Contributors
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<a
										className="md:hidden"
										href={`https://www.kogia.org/projects`}
									>
										<Icon size="md" name="external-link">
											Projects
										</Icon>
									</a>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<a
										className="md:hidden"
										href={`https://www.kogia.org/fellowships`}
									>
										<Icon size="md" name="external-link">
											Fellowships
										</Icon>
									</a>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<a className="md:hidden" href={`https://www.kogia.org/about`}>
										<Icon size="md" name="external-link">
											About
										</Icon>
									</a>
								</DropdownMenuItem>
								<DropdownMenuSeparator className="flex" />
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'image',
								) && (
									<DropdownMenuItem asChild>
										<Link
											prefetch="intent"
											to={`/contributors/${user?.username}`}
										>
											<Icon size="md" name="avatar">
												Your Profile
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								<DropdownMenuItem asChild>
									<Link prefetch="intent" to={`/settings/collections`}>
										<Icon size="md" name="layers">
											Your Collections
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'image',
								) && (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/settings/bulk-upload/photo`}>
											<Icon size="md" name="camera">
												Upload Photos
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'video',
								) && (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/settings/bulk-upload/video`}>
											<Icon size="md" name="video">
												Upload Videos
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								<DropdownMenuItem asChild>
									<Link prefetch="intent" to={`/settings/collections/new`}>
										<Icon size="md" name="layers">
											Create Collection
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<Link prefetch="intent" to={`/settings/profile`}>
										<Icon size="md" name="pencil-2">
											Edit Profile
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										prefetch="intent"
										to={`/settings/profile`}
										className="pr-2"
									>
										<Icon size="md" name="gear">
											Settings
										</Icon>
									</Link>
								</DropdownMenuItem>
								{isAdmin && (
									<>
										<DropdownMenuItem asChild>
											<Link prefetch="intent" to={`/admin`} className="pr-2">
												<Icon size="md" name="lock-closed">
													Admin
												</Icon>
											</Link>
										</DropdownMenuItem>
									</>
								)}
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'image',
								) ? (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/tos/contributors`}>
											<Icon size="md" name="book">
												Terms of Service
											</Icon>
										</Link>
									</DropdownMenuItem>
								) : (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/tos/members`}>
											<Icon size="md" name="book">
												Terms of Service
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuItem
									asChild
									// this prevents the menu from closing before the form submission is completed
									onSelect={event => {
										event.preventDefault()
										submit(formRef.current)
									}}
								>
									<Form action="/logout" method="POST" ref={formRef}>
										<Icon size="md" name="exit">
											<button type="submit">Logout</button>
										</Icon>
									</Form>
								</DropdownMenuItem>
								{/* <DropdownMenuSeparator />
								<DropdownMenuItem
									asChild
									onSelect={event => {
										event.preventDefault()
									}}
								>
									<fetcher.Form action="/" method="POST" ref={themeRef}>
										<input type="hidden" name="theme" value={nextMode} />
										<Icon size="md" name={nextItem}>
											<button type="submit">
												{nextMode === 'dark' && 'Night Theme'}
												{nextMode === 'light' && 'Day Theme'}
												{nextMode === 'system' && 'System Default'}
											</button>
										</Icon>
									</fetcher.Form>
								</DropdownMenuItem> */}
							</DropdownMenuContent>
						</DropdownMenuPortal>
					</DropdownMenu>
				)}
			</div>
		</header>
	)
}
